import React, { useEffect, useState } from 'react';
import { X, FolderKanban, Sparkles, Loader2 } from 'lucide-react';
import { analyticsHubService } from '../../../../../components/api/analyticsHubService';
import type { Theme, ThemeColors, ReportCollection } from './types';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-800 border-neutral-600 text-white placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
  },
};

interface ReportCollectionModalProps {
  theme?: Theme;
  collection?: ReportCollection | null;
  onClose: () => void;
  onSaved: (result: { isCreate: boolean; name: string; id?: string }) => void;
}

const ReportCollectionModal: React.FC<ReportCollectionModalProps> = ({
  theme = 'dark',
  collection,
  onClose,
  onSaved,
}) => {
  const colors = THEME_CONFIG[theme];
  const isEdit = !!collection;
  const [name, setName] = useState(collection?.name ?? '');
  const [description, setDescription] = useState(collection?.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Collection name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await analyticsHubService.upsertAnalyticsHubCollection({
        _id: collection?.id,
        collection_name: trimmed,
        description: description.trim() || undefined,
      });
      onSaved({
        isCreate: !isEdit,
        name: trimmed,
        id: collection?.id,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save collection. Please try again.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`w-full max-w-md rounded-xl border shadow-2xl ${colors.border} ${colors.primary}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="collection-modal-title"
      >
        <div className={`flex items-center justify-between px-5 py-4 border-b ${colors.border}`}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <FolderKanban className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 id="collection-modal-title" className={`text-sm font-semibold ${colors.text.primary}`}>
                {isEdit ? 'Edit Collection' : 'New Collection'}
              </h2>
              <p className={`text-[10px] ${colors.text.muted}`}>
                {isEdit ? 'Update name and description' : 'Group related reports together'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1.5 rounded-md ${colors.hover} ${colors.text.muted}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label htmlFor="collection-name" className={`block text-[11px] font-medium ${colors.text.secondary} mb-1.5`}>
              Collection name
            </label>
            <input
              id="collection-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="e.g. Executive Dashboards"
              className={`w-full px-3 py-2 rounded-lg border text-sm ${colors.input}`}
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="collection-desc" className={`block text-[11px] font-medium ${colors.text.secondary} mb-1.5`}>
              Description <span className={colors.text.muted}>(optional)</span>
            </label>
            <textarea
              id="collection-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What kind of reports belong here?"
              rows={3}
              className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${colors.input}`}
            />
          </div>

          {!isEdit && (
            <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border ${colors.border} ${colors.secondary}`}>
              <Sparkles className="w-3.5 h-3.5 text-violet-400 flex-shrink-0 mt-0.5" />
              <p className={`text-[10px] leading-relaxed ${colors.text.muted}`}>
                Use clear names like &quot;Sales KPIs&quot; or &quot;Support Analytics&quot; so teams can find reports quickly.
              </p>
            </div>
          )}

          {error && (
            <p className="text-[11px] text-red-400">{error}</p>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className={`px-3 py-1.5 rounded-md text-[11px] font-medium ${colors.hover} ${colors.text.secondary} disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-[11px] font-medium transition-colors flex items-center gap-1.5"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Collection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportCollectionModal;
